import React, { useEffect, useRef, useState } from 'react';
import { LO_APP_ID } from '../config.js';

/**
 * Multi-component Lightning Out host.
 *
 * Mounts TWO top-level LWCs as siblings inside a single
 * <lightning-out-application>:
 *   - c-search-results (list)
 *   - c-record-detail  (detail of the record React has selected)
 *
 * Rules of thumb used here (see discussion in README):
 *   1. BOTH components are registered in the LO App's `components` field.
 *   2. Each host-side element is created exactly ONCE; we never
 *      remove+recreate on state change — only update attributes.
 *   3. Both elements stay in the layout (no `display: none`) so LO 2.0
 *      can complete iframe bootstrap for each.
 *
 * React <-> LWC bridge:
 *   React -> list:   attribute `keyword`
 *   React -> detail: attributes `record-id`, `record-name`, `sobject-type`
 *   list -> React:   CustomEvent `recordselect` { id, name, sobjectType }
 */
export default function SplitLightningOutHost({
  keyword,
  selectedRecord,
  onRecordSelect
}) {
  const frameRef = useRef(null);
  const listRef = useRef(null);
  const detailRef = useRef(null);
  const [status, setStatus] = useState('Initializing…');
  const [error, setError] = useState(null);

  // Latest-callback ref so the one-time listener always calls current fn.
  const onRecordSelectRef = useRef(onRecordSelect);
  useEffect(() => { onRecordSelectRef.current = onRecordSelect; }, [onRecordSelect]);

  useEffect(() => {
    let cancelled = false;

    async function waitForSdk(timeoutMs = 10000) {
      const start = Date.now();
      while (!customElements.get('lightning-out-application')) {
        if (Date.now() - start > timeoutMs) {
          throw new Error('Lightning Out SDK load timeout');
        }
        await new Promise((r) => setTimeout(r, 100));
      }
    }

    (async () => {
      try {
        setStatus('📡 Getting frontdoor URL…');
        const res = await fetch('/api/get-frontdoor-url', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        });
        if (res.status === 401) {
          setError('Session expired. Please log in again.');
          return;
        }
        if (!res.ok) throw new Error(`Frontdoor request failed: ${res.status}`);
        const { frontdoor_uri } = await res.json();
        if (cancelled) return;

        setStatus('⏳ Waiting for Lightning Out SDK…');
        await waitForSdk();
        if (cancelled) return;

        setStatus('🔐 Creating Lightning Out elements…');
        const frame = frameRef.current;
        frame.innerHTML = '';

        const loApp = document.createElement('lightning-out-application');
        loApp.setAttribute('app-id', LO_APP_ID);
        loApp.setAttribute('frontdoor-url', frontdoor_uri);
        // Register both components with the LO container. (The LO App record
        // in Setup must ALSO list both under Components.)
        loApp.setAttribute('components', 'c-search-results,c-record-detail');
        frame.appendChild(loApp);

        // ---- List pane
        const listPane = document.createElement('div');
        listPane.className = 'split-pane split-pane--list';
        const listEl = document.createElement('c-search-results');
        if (keyword) listEl.setAttribute('keyword', keyword);
        // Per LO 2.0: listeners must be on the host-side component element.
        listEl.addEventListener('recordselect', (e) => {
          if (e.detail) onRecordSelectRef.current?.(e.detail);
        });
        listPane.appendChild(listEl);
        frame.appendChild(listPane);
        listRef.current = listEl;

        // ---- Detail pane
        const detailPane = document.createElement('div');
        detailPane.className = 'split-pane split-pane--detail';
        const detailEl = document.createElement('c-record-detail');
        if (selectedRecord?.id) detailEl.setAttribute('record-id', selectedRecord.id);
        if (selectedRecord?.name) detailEl.setAttribute('record-name', selectedRecord.name);
        if (selectedRecord?.sobjectType) detailEl.setAttribute('sobject-type', selectedRecord.sobjectType);
        // Ignore `hostnavigate` here — in split mode there's nowhere to go back to.
        detailPane.appendChild(detailEl);
        frame.appendChild(detailPane);
        detailRef.current = detailEl;

        setStatus('🔗 Lightning Out mounted — waiting for components…');
      } catch (e) {
        setError(e.message);
      }
    })();

    const onReady = () => setStatus('✅ Lightning Out session established');
    const onCompReady = () => setStatus('🎉 LWCs rendered');
    const onAppErr = (e) =>
      setError('Application error: ' + (e.detail?.message || 'unknown'));
    const onCompErr = (e) =>
      setError('Component error: ' + (e.detail?.message || 'unknown'));

    window.addEventListener('lo.application.ready', onReady);
    window.addEventListener('lo.component.ready', onCompReady);
    window.addEventListener('lo.application.error', onAppErr);
    window.addEventListener('lo.component.error', onCompErr);

    return () => {
      cancelled = true;
      window.removeEventListener('lo.application.ready', onReady);
      window.removeEventListener('lo.component.ready', onCompReady);
      window.removeEventListener('lo.application.error', onAppErr);
      window.removeEventListener('lo.component.error', onCompErr);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync keyword -> list element (attribute only; no DOM churn).
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    if (keyword) el.setAttribute('keyword', keyword);
    else el.removeAttribute('keyword');
  }, [keyword]);

  // Sync selectedRecord -> detail element.
  useEffect(() => {
    const el = detailRef.current;
    if (!el) return;
    if (selectedRecord?.id) el.setAttribute('record-id', selectedRecord.id);
    else el.removeAttribute('record-id');
    if (selectedRecord?.name) el.setAttribute('record-name', selectedRecord.name);
    else el.removeAttribute('record-name');
    if (selectedRecord?.sobjectType) el.setAttribute('sobject-type', selectedRecord.sobjectType);
    else el.removeAttribute('sobject-type');
  }, [selectedRecord]);

  return (
    <>
      {error ? (
        <div className="status error">❌ {error}</div>
      ) : (
        <div className="status">{status}</div>
      )}
      <div className="lo-frame lo-frame--split" ref={frameRef} />
    </>
  );
}
